/**
 * PortalPlans — choose a plan and pay (Stage 3.4, 29 Sep 2026).
 * Look taken from the example CheckoutPage; payment itself happens on Stripe's own checkout page
 * (Cloud Function createCheckoutSession, codebase "portal"). Prices come from plans.json (same file as Stripe).
 *
 * Props:
 *   user          signed-in Firebase user or null
 *   userData      users/{uid} document data (for an existing subscription)
 *   onNeedLogin   ({planId, period}) => void — a signed-out visitor picked a plan: go to sign-up with it
 *   onManage      () => void          — go to the subscription panel
 *   onSticker     () => void          — open the free sticker program (advertisers)
 */
import React, { useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import cfg from './plans.json';
import './PortalPlans.css';

export const INCLUDES = {
  station: ['Upload your recordings (public or private)', 'Results for every broadcast', 'Station login in the Cetalingua FM app'],
  decoder: ['Analyse community recordings', 'Mark detections, call types with cheat sheets', 'Summary export'],
  researcher: ['Everything in Decoder', 'All measurements, click card, Wigner-Ville', 'Full export (tables, figures, edits)', 'Call library (view)'],
  advertiser: ['Unlimited posts: featured media, what\'s new, hot deals (approved by us)', '1 push notification per month', 'Self-service dashboard'],
};
export const PRODUCT_NAMES = { station: 'Station', decoder: 'Decoder', researcher: 'Researcher', advertiser: 'Advertiser' };

const money = (n) => `$${n.toFixed(2)}`;
const ACTIVE = ['active', 'trialing', 'past_due'];

const PortalPlans = ({ user, userData, onNeedLogin, onManage, onSticker }) => {
  const [period, setPeriod] = useState('m1');
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState('');
  const months = cfg.periods[period].months;
  const hasSub = ACTIVE.includes(userData?.portalSubscription?.status);

  const singles = cfg.plans.filter((p) => !p.id.includes('_'));
  const bundles = cfg.plans.filter((p) => p.id.includes('_'));

  const choose = async (plan) => {
    setError('');
    if (!user) { onNeedLogin?.({ planId: plan.id, period }); return; }
    if (hasSub) { onManage?.(); return; }
    setBusy(plan.id);
    try {
      const start = httpsCallable(getFunctions(), 'createCheckoutSession');
      const res = await start({ planId: plan.id, period });
      window.location.href = res.data.url;               // Stripe's checkout page
    } catch (e) {
      setError(e.message || 'Could not start the payment. Please try again.');
      setBusy(null);
    }
  };

  const sumOfSingles = (plan) => plan.products
    .filter((p) => !(p === 'decoder' && plan.products.includes('researcher')))
    .reduce((s, p) => s + (cfg.plans.find((x) => x.id === p)?.prices[period] || 0), 0);

  const Card = ({ plan, bundle }) => {
    const price = plan.prices[period];
    const save = bundle ? sumOfSingles(plan) - price : 0;
    const shown = plan.products.filter((p) => !(p === 'decoder' && plan.products.includes('researcher')));
    return (
      <div className={`pp-card ${plan.id === 'station' ? 'pp-featured' : ''}`}>
        {plan.id === 'station' && <div className="pp-badge">MOST POPULAR</div>}
        {plan.trialDays > 0 && !userData?.portalTrialUsed && <div className="pp-badge pp-trial">{plan.trialDays}-DAY FREE TRIAL</div>}
        <h2>{plan.name}</h2>
        <div className="pp-price">
          <span className="pp-amount">{money(price)}</span>
          <span className="pp-per">/ {months === 1 ? 'month' : `${months} months`}</span>
        </div>
        <p className="pp-sub">
          {months > 1 ? `${money(price / months)} per month` : 'Cancel anytime'}
          {save > 0.5 ? ` · save ${money(save)}` : ''}
        </p>
        {bundle ? (
          <ul className="pp-list">
            <li>Everything in:</li>
            {shown.map((p) => <li key={p}>✓ <strong>{PRODUCT_NAMES[p]}</strong></li>)}
          </ul>
        ) : (
          <ul className="pp-list">
            {INCLUDES[plan.id].map((t) => <li key={t}>✓ {t}</li>)}
          </ul>
        )}
        <button className="pp-btn" disabled={!!busy} onClick={() => choose(plan)}>
          {busy === plan.id ? 'Opening secure payment…' : hasSub ? 'Change in Manage subscription' :
            plan.trialDays > 0 && !userData?.portalTrialUsed ? 'Start free trial' : `Choose ${plan.name}`}
        </button>
        {plan.id === 'advertiser' && (
          <button className="pp-link" onClick={() => onSticker?.()}>
            Whale watching company? Get 3 months free with our sticker program →
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="pp-page">
      <div className="pp-header">
        <h1>Choose your Cetalingua plan</h1>
        <p>One login for everything. Combine products in a bundle and save.</p>
      </div>

      {hasSub && (
        <div className="pp-note">
          You already have a subscription. To change plan or billing period use{' '}
          <button className="pp-inline" onClick={() => onManage?.()}>Manage subscription</button>.
        </div>
      )}

      <div className="pp-periods" role="tablist">
        {Object.entries(cfg.periods).map(([k, v]) => (
          <button key={k} role="tab" aria-selected={period === k}
                  className={`pp-period ${period === k ? 'pp-on' : ''}`} onClick={() => setPeriod(k)}>
            {v.label}
          </button>
        ))}
      </div>

      {error && <div className="pp-error">{error}</div>}

      <h3 className="pp-group">Single products</h3>
      <div className="pp-grid">{singles.map((p) => <Card key={p.id} plan={p} />)}</div>

      <h3 className="pp-group">Bundles</h3>
      <div className="pp-grid">{bundles.map((p) => <Card key={p.id} plan={p} bundle />)}</div>

      <div className="pp-foot">
        <p><strong>Academic or non-profit?</strong> Write to us for a 20% code and enter it on the payment page.</p>
        <p>Payments are handled by Stripe. Prices in USD. Multi-month plans renew for the same period unless you turn
           renewal off in Manage subscription.</p>
      </div>
    </div>
  );
};

export default PortalPlans;
